/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  DateFilterModel,
  FilterModel,
  IServerSideGetRowsRequest,
  ISimpleFilterModel,
  NumberFilterModel,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-community';
import { TypesaurusCore, TypesaurusQuery } from 'typesaurus';
import { FirestoreGenGroupInterface } from './firestore-group.gen.service.interface';
import { AbstractQueryParams } from './query-params';
import {
  AllRequired,
  OffSubscriptionWithCatch,
  OrderDirection,
  OrOrOrderQuery,
  PaginationStateExtension,
  QueryArrayField,
  QueryFieldKey1,
  QueryFieldValue,
  QueryPrimitiveField,
  SaurusGroup,
  SaurusHelper,
  SaurusUnionVariableWideModelType,
  SaurusWriteDataWideWithProps,
  SaurusWriteHelpers,
  TypesaurusAssignArgDataModel,
  TypesaurusCoreDoc,
  TypesaurusCoreRef,
  TypesaurusSchema,
  TypesaurusUpdateArg,
  TypesaurusUpdateArgData,
  UnionKeys,
} from './typesaurus.interface';

/** The `QueryPrimitiveField` methods that take a single comparison value. */
type ComparisonOperator = 'lt' | 'lte' | 'eq' | 'not' | 'gt' | 'gte';

/**
 * ag-grid filter types that map one to one onto a single Typesaurus comparison.
 * `startsWith` / `endsWith` are approximated by a range bound, as they were before.
 */
const comparisonByFilterType = new Map<ISimpleFilterModel['type'], ComparisonOperator>([
  ['lessThan', 'lt'],
  ['lessThanOrEqual', 'lte'],
  ['equals', 'eq'],
  ['notEqual', 'not'],
  ['greaterThan', 'gt'],
  ['greaterThanOrEqual', 'gte'],
  ['endsWith', 'lte'],
  ['startsWith', 'gte'],
]);

/**
 * The value an order cursor accepts for a field of `Def`, spelled out for the same
 * `Parent` / `Key` pair `QueryPrimitiveField<Def>` is built with.
 */
type OrderCursorValueOf<Def extends TypesaurusCore.DocDef> = TypesaurusQuery.OrderCursorValue<
  Def,
  Def['Model'],
  UnionKeys<AllRequired<Def['Model']>>
>;

@Injectable({
  providedIn: 'root',
})
export abstract class AbstractGenFirestoreGroupService<
  Sma extends TypesaurusSchema,
  ParentKey extends keyof Sma,
  SubKey extends keyof Sma[keyof Sma]['sub'],
  SubDef extends TypesaurusCore.DocDef = Sma[ParentKey]['sub'][SubKey]['Def'],
  ParentDef extends TypesaurusCore.DocDef = Sma[ParentKey]['Def'],
> implements FirestoreGenGroupInterface<ParentDef, SubDef> {
  protected selfOrm: SaurusGroup<SubDef>;
  protected parentOrmWithIdCall: (userId: Sma[ParentKey]['Id']) => TypesaurusCore.Collection<SubDef>;

  constructor(
    addressOrm: SaurusGroup<SubDef>,
    userOrm: (userId: Sma[ParentKey]['Id']) => TypesaurusCore.Collection<SubDef>,
  ) {
    this.selfOrm = addressOrm;
    this.parentOrmWithIdCall = userOrm;
  }

  async GetAll(): Promise<TypesaurusCoreDoc<SubDef>[]> {
    return await this.selfOrm.all();
  }

  OnGetAll(callback: (arrayOfObjects: Array<TypesaurusCoreDoc<SubDef>>) => void): OffSubscriptionWithCatch {
    return this.selfOrm.all().on(callback);
  }

  OnGet(id: string, callback: (arrayOfObjects: Array<TypesaurusCoreDoc<SubDef>>) => void): OffSubscriptionWithCatch {
    return this.parentOrmWithIdCall(id).all().on(callback);
  }

  async QueryAll(queryHelpers: Array<AbstractQueryParams<SubDef>>): Promise<TypesaurusCoreDoc<SubDef>[]> {
    return await this.selfOrm.query((q) =>
      queryHelpers.map((x: AbstractQueryParams<SubDef>) => {
        switch (x.field?.length) {
          case 4:
            return (q.field(x.field[0], x.field[1], x.field[2], x.field[3]) as QueryPrimitiveField<SubDef>)?.[
              x.operator as keyof QueryPrimitiveField<SubDef>
            ](x.value as any);
          case 3:
            return (q.field(x.field[0], x.field[1], x.field[2]) as QueryPrimitiveField<SubDef>)?.[
              x.operator as keyof QueryPrimitiveField<SubDef>
            ](x.value as any);
          case 2:
            return (q.field(x.field[0], x.field[1]) as QueryPrimitiveField<SubDef>)?.[
              x.operator as keyof QueryPrimitiveField<SubDef>
            ](x.value as any);
          case 1:
            return (q.field(x.field[0] as QueryFieldKey1<SubDef>) as QueryPrimitiveField<SubDef>)?.[
              x.operator as keyof QueryPrimitiveField<SubDef>
            ](x.value as any);
          default:
            break;
        }
        if (x.orderDirection) {
          return (q.field(x.orderBy as QueryFieldKey1<SubDef>) as QueryPrimitiveField<SubDef>).order(x?.orderDirection);
        }
        return undefined;
      }),
    );
  }

  OnQueryAll(queryHelpers: SaurusHelper<SubDef>): Promise<TypesaurusCoreDoc<SubDef>[]> {
    return Promise.reject(new Error(`Method not implemented.${queryHelpers}`));
  }

  async Create(
    ref: TypesaurusCoreRef<ParentDef>,
    assignData: TypesaurusAssignArgDataModel<SubDef>,
  ): Promise<TypesaurusCoreRef<SubDef>> {
    const createdId = await this.parentOrmWithIdCall(ref.id).add(
      ($: SaurusWriteHelpers<SaurusUnionVariableWideModelType<SubDef>>) =>
        ({
          ...(assignData as TypesaurusAssignArgDataModel<SubDef>),
          createdAt: $.serverDate(),
        }) as SaurusWriteDataWideWithProps<SaurusUnionVariableWideModelType<SubDef>>,
    );
    console.log({ createdId });
    return createdId;
  }
  SetQuery(
    id: string,
    request: Omit<
      IServerSideGetRowsRequest & PaginationStateExtension,
      'rowGroupCols' | 'valueCols' | 'pivotCols' | 'pivotMode' | 'groupKeys'
    >,
  ): {
    runs: OrOrOrderQuery<SubDef>[];
    direction: string;
  } {
    const queryBuilder = this.parentOrmWithIdCall(id).query.build();
    const runs = [];
    for (const [key, filterModelKey] of Object.entries(request.filterModel ?? {})) {
      filterModelKey.conditions = [{ ...filterModelKey }];
      if (filterModelKey?.operator === 'OR') {
        runs.push(
          queryBuilder.or(
            filterModelKey.conditions.map(
              (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) => {
                return this.filterTypeHelper(element, queryBuilder, key);
              },
            ),
          ),
        );
      } else {
        filterModelKey.conditions.forEach(
          (element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel) =>
            this.filterTypeHelper(element, queryBuilder, key).forEach((whereQuery) => runs.push(whereQuery)),
        );
      }
    }
    let direction = 'asc';
    if (
      (request?.filterModel as FilterModel)['createdAt'] &&
      !request.sortModel.find((sortEntry) => sortEntry.colId === 'createdAt')
    ) {
      request.sortModel.push({
        colId: 'createdAt',
        sort: 'asc',
      });
    }
    if (request?.endRow && typeof request?.startRow === 'number') {
      const isFirstOrLast = request.endRow >= request.count || request.startRow === 0;
      if (isFirstOrLast) {
        if (request.sortModel.length) {
          request.sortModel.forEach((sortEntry) => {
            const sortDir = request.startRow === 0 ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
            runs.push(
              (queryBuilder.field(sortEntry.colId as QueryFieldKey1<SubDef>) as QueryPrimitiveField<SubDef>).order(
                sortDir,
              ),
            );
          });
        } else if (!(request?.filterModel as FilterModel)['createdAt']) {
          runs.push(
            (queryBuilder.field(queryBuilder.docId()) as QueryPrimitiveField<SubDef>).order(
              request.startRow === 0 ? 'desc' : 'asc',
            ),
          );
        }
        direction = request.startRow === 0 ? 'desc' : 'asc';
      } else {
        const cursor = {
          direction: request?.endRow && request.data[request?.endRow] ? 'asc' : 'desc',
          startDocAt:
            request?.endRow && request.data[request?.endRow]?.ref?.id
              ? request.data[request.endRow]
              : request.data[request.startRow],
        };
        console.log('abs-g-0', { queryBuilder });
        runs.push(
          (queryBuilder.field(queryBuilder.docId()) as QueryPrimitiveField<SubDef>).order(
            cursor.direction as OrderDirection,
            queryBuilder.startAt(cursor.startDocAt.ref.id),
          ),
        );
        request.sortModel
          .map((sortEntry, sortIndex) => {
            const sortDir = cursor.direction === 'desc' ? sortEntry.sort : sortEntry.sort === 'asc' ? 'desc' : 'asc';
            const fieldPath = sortEntry.colId.split('.', 4);
            let startDocAt = (cursor.startDocAt as Record<string, unknown>)[fieldPath[0]];
            for (let i = 1; i < fieldPath.length; i++) {
              startDocAt = (startDocAt as Record<string, unknown>)[fieldPath[i]];
            }
            const sortField = queryBuilder.field(
              sortEntry.colId as QueryFieldKey1<SubDef>,
            ) as QueryPrimitiveField<SubDef>;
            if (sortIndex === 0) {
              console.log('abs-g-1', { queryBuilder });

              return sortField.order(
                sortDir,
                queryBuilder.startAt<SubDef['Model'], UnionKeys<AllRequired<SubDef['Model']>>>(
                  startDocAt as OrderCursorValueOf<SubDef>,
                ),
              );
            }
            return sortField.order(sortDir);
          })
          .forEach((orderQuery) => runs.push(orderQuery));
        direction = cursor.direction;
      }
    }
    return { runs, direction };
  }

  SetLimit(id: string, request: IServerSideGetRowsRequest & PaginationStateExtension): OrOrOrderQuery<SubDef>[] {
    const runs = [];
    const queryBuilder = this.parentOrmWithIdCall(id).query.build();
    const rowCount =
      (request?.endRow ?? 0) >= request.count
        ? (request.count ?? 0) - (request.startRow ?? 0)
        : (request.endRow ?? 0) - (request.startRow ?? 0) + 1;

    runs.push(queryBuilder.limit(rowCount));
    return runs;
  }

  async Set(
    ref: TypesaurusCoreRef<SubDef>,
    data: TypesaurusAssignArgDataModel<SubDef>,
  ): Promise<TypesaurusCoreRef<SubDef>> {
    return await ref.set(
      ($: SaurusWriteHelpers<SaurusUnionVariableWideModelType<SubDef>>) =>
        ({
          ...data,
          createdAt: $.serverDate(),
        }) as SaurusWriteDataWideWithProps<SaurusUnionVariableWideModelType<SubDef>>,
    );
  }

  async Update(ref: TypesaurusCoreRef<SubDef>, updateArg: TypesaurusUpdateArgData<SubDef>) {
    return await ref.update(($) => ({ ...(updateArg as TypesaurusUpdateArgData<SubDef>), updatedAt: $.serverDate() }));
  }

  UpdateOrSet(
    id: TypesaurusCoreRef<SubDef>['id'],
    data: TypesaurusUpdateArg<SubDef>,
  ): Promise<TypesaurusCoreRef<SubDef>> {
    return Promise.reject(new Error(`Method not implemented.${{ id, data }}`));
  }

  async Delete(ref: TypesaurusCoreRef<SubDef>): Promise<TypesaurusCoreRef<SubDef>> {
    return await ref.remove();
  }

  SoftDelete(ref: TypesaurusCoreRef<SubDef>): Promise<TypesaurusCoreRef<SubDef>> {
    return Promise.reject(new Error(`Method not implemented.${{ ref }}`));
  }

  private filterTypeHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel & SetFilterModel,
    helpers: SaurusHelper<SubDef>,
    fields: string,
  ) {
    const field = this.whereField(helpers, fields);
    if (element.filterType === 'set') {
      return element.values.length ? [field.in(element.values as QueryFieldValue<SubDef>[])] : [];
    }
    const comparison = comparisonByFilterType.get(element.type);
    if (comparison) {
      return [field[comparison]((element?.dateFrom ?? element?.filter) as QueryFieldValue<SubDef>)];
    }
    return this.matchFilterHelper(element, field);
  }

  private matchFilterHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel,
    field: QueryPrimitiveField<SubDef> & QueryArrayField<SubDef>,
  ) {
    const filterValue = element?.dateFrom ?? element?.filter;
    switch (element.type) {
      case 'contains':
        return [field.in((Array.isArray(filterValue) ? filterValue : [filterValue]) as QueryFieldValue<SubDef>[])];
      case 'notContains':
        return [field.notIn(filterValue as any)];
      case 'inRange':
        return this.inRangeFilterHelper(element, field);
      case 'notBlank':
        return [field.notIn([null, undefined, ''] as QueryFieldValue<SubDef>[])];
      case 'blank':
      case 'empty':
        return [field.in(['', 'null', null, undefined] as QueryFieldValue<SubDef>[])];
      default:
        return [];
    }
  }

  private inRangeFilterHelper(
    element: DateFilterModel & TextFilterModel & NumberFilterModel,
    field: QueryPrimitiveField<SubDef> & QueryArrayField<SubDef>,
  ) {
    if (element?.dateFrom && element?.dateTo) {
      return [
        field.gte(new Date(element?.dateFrom) as QueryFieldValue<SubDef>),
        field.lte(new Date(element?.dateTo) as QueryFieldValue<SubDef>),
      ];
    }
    return [
      field.gte(element?.filter as QueryFieldValue<SubDef>),
      field.lte(element?.filterTo as QueryFieldValue<SubDef>),
    ];
  }

  /** Bridges an ag-grid column key, only known at runtime, onto the typed field selector. */
  private whereField(
    helpers: SaurusHelper<SubDef>,
    field: string,
  ): QueryPrimitiveField<SubDef> & QueryArrayField<SubDef> {
    return helpers.field(field as QueryFieldKey1<SubDef>) as QueryPrimitiveField<SubDef> & QueryArrayField<SubDef>;
  }
}
